import { CONSENT_EVENT, ConsentChoices } from './types'
import { enableRumTelemetry } from '../telemetry/faro'
import { enablePlausible } from '../analytics/plausible'

// A granted category loads its scripts. Categories can only be added here, never
// revoked: once a third-party script is on the page it cannot be truly unloaded,
// so revoking consent takes effect on the next full load (the script is simply
// not re-injected). This is the conventional behaviour for tag-style loaders.
//
// Faro is on the same footing, with one difference worth knowing: it is ALREADY
// running by the time this is called (the shell starts it before anything else, to
// catch startup crashes and to trace the startup requests), but only as a fault
// reporter — errors and request traces, no session id, no vitals, no view tracking.
// `analytics` is what promotes it to full RUM. See telemetry/faro.ts for the split
// and why faults sit outside the gate.
//
// `enablePlausible` both injects the script and opens the goal gate that
// `app.trackGoal` checks (see analytics/plausible.ts) — the two must flip together,
// or pilets would pile goals into the inline stub's queue before a decision.
function apply (choices?: ConsentChoices | null): void {
  if (choices?.analytics) {
    enablePlausible()
    enableRumTelemetry()
  }
}

/**
 * Bind the third-party loaders to the consent decision. Call once at shell
 * startup. Nothing loads until consent is granted:
 *
 *  - a decision already broadcast this load (a stored, still-valid record that
 *    {@link ConsentProvider} re-applies on mount) is read from `__medialibConsent`;
 *  - later decisions arrive on the {@link CONSENT_EVENT} window event.
 *
 * This is the counterpart to `applyConsent` in ConsentProvider: that records and
 * broadcasts, this reacts and loads.
 */
export function initConsentLoaders (): void {
  if (typeof window === 'undefined') return
  apply((window as unknown as { __medialibConsent?: ConsentChoices }).__medialibConsent)
  window.addEventListener(CONSENT_EVENT, event => {
    apply((event as CustomEvent<ConsentChoices>).detail)
  })
}
